import React, { FC } from 'react'
import Grid from '@sport1/react-elements/Grid'
import Touchable from '@sport1/react-elements/Touchable'
import BasicText from '../Theme/Fonts/BasicText'
import SmallButton from '../BaseComponents/Button/Standard/Small'
import BigTeaserHeadline from '../Theme/Fonts/BigTeaserHeadline'
import { FixedMarginProps } from '../utils/styling/StylingHelper'
import { RequireAtLeastOne } from '../utils/types/requireAtLeastOne'
import { TestID } from '../utils/PropTypesDefinitions'
import ProfileImage from './Image'
import RightOptionalProfileField from './RightOptionalField'
import LabelInfo from './LabelInfo'

export type ProfileProps = FixedMarginProps &
    TestID & {
        /** type to determine specific UI changes, e.g. no border for TEAM images */
        type: 'PERSON' | 'TEAM'
        /** the title of the profile, e.g. name of the player/author/team */
        title: string
        /** a small label to describe the profile more in detail
         * can show an image if an imageUrl was passed
         * */
        labelProps?: {
            text: string
            imageUrl?: string
            alt?: string
            onPress?: () => void
            href?: string
        }
        /** the center image of the profile, e.g. picture of player/author/team */
        imageProps: {
            imageUrl?: string
            alt?: string
        }
        /** buttonProps to show a button with a specific text and callback function */
        buttonProps?: RequireAtLeastOne<
            {
                text: string
                href?: string
                onPress: () => void
            },
            'onPress' | 'href'
        >
        /** a text on the left side of the component, mostly used to show the jerseyNumber of a player or the placing of a team */
        leftOptionalFieldText?: string
        /** the props to show an image and a text (with value and metric), mostly used to show nationality and age of a player/team */
        rightOptionalFieldProps?: {
            imageUrl: string
            alt?: string
            value?: string
            metric?: string
        }
        /**
         * function called when pressed on image or title
         * @TJS-type function
         */
        onProfilePress?: () => void
        /** href string for press on image or title */
        profileHref?: string
    }

const Profile: FC<ProfileProps> = ({
    type,
    title,
    labelProps,
    imageProps,
    buttonProps,
    leftOptionalFieldText,
    rightOptionalFieldProps,
    onProfilePress,
    profileHref,
    testID,
    ...marginProps
}) => (
    <Grid testID={testID} noExpand center paddingY={5} {...marginProps}>
        <Grid noExpand horizontal>
            <Grid noExpand marginRight={5} width={40}>
                {leftOptionalFieldText ? (
                    <BigTeaserHeadline numberOfLines={1} textAlign="center" color="onyx-5">
                        {leftOptionalFieldText}
                    </BigTeaserHeadline>
                ) : null}
            </Grid>
            <Touchable href={profileHref} onPress={onProfilePress}>
                <ProfileImage type={type} {...imageProps} />
            </Touchable>
            <Grid noExpand marginLeft={5} width={40}>
                {rightOptionalFieldProps ? (
                    <RightOptionalProfileField {...rightOptionalFieldProps} />
                ) : null}
            </Grid>
        </Grid>
        <Touchable href={profileHref} onPress={onProfilePress}>
            <BasicText
                bold
                fontSize={['FS16', 'FS16', 'FS18']}
                lineHeight={['LH22', 'LH22', 'LH24']}
                numberOfLines={2}
                paddingBottom={2}
                textAlign="center"
            >
                {title}
            </BasicText>
        </Touchable>
        <Grid noExpand minHeight={[14, 14, 16]}>
            {labelProps ? <LabelInfo {...labelProps} /> : null}
        </Grid>
        {buttonProps ? (
            <Grid noExpand width="100%" marginTop={4} paddingX={5}>
                <SmallButton expand backgroundColor="moon-2" {...buttonProps}>
                    {buttonProps.text}
                </SmallButton>
            </Grid>
        ) : null}
    </Grid>
)

export default Profile
